// src/components/Lobby/LobbyPageV2.tsx
// ⚠️ Version proposée - NE PAS UTILISER EN PRODUCTION AVANT VALIDATION
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, updateProfile } from '../../config/firebase.ts';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.tsx';
import LobbyRoomPage from './LobbyRoomPage.tsx';
import HelpPopover from '../Tutorial/HelpPopover.tsx';
import { SoundControl } from '../Sound/SoundControl.tsx';
import CategoryCreator from '../CategoryCreator/CategoryCreator.tsx';
import CategoryExplorer from '../CategoryCreator/CategoryExplorer.tsx';
import CategoryMyCreations from '../CategoryCreator/CategoryMyCreations.tsx';
import PlayerStats from '../Statistics/PlayerStats.tsx';
import DecorationSelector from '../Common/DecorationSelector.tsx';
import PlayerName from '../Common/PlayerName.tsx';
import { useDisclosure, Text, useToast } from '@chakra-ui/react';
import './LobbyPageV2.css';

interface LobbyPageProps {
  user: any;
  handleLogout: () => void;
  onStartLocalGame: () => void;
  onStartMultiplayerGame?: (data: any) => void;
  urlLobbyCode?: string;
}

type TabType = 'solo' | 'multi' | 'community' | 'profile';

const LobbyPageV2: React.FC<LobbyPageProps> = ({ 
  user, 
  handleLogout, 
  onStartLocalGame, 
  onStartMultiplayerGame, 
  urlLobbyCode: propLobbyCode 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code: paramLobbyCode } = useParams<{ code?: string }>();
  const [roomCode, setRoomCode] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(user.displayName || '');
  const [lobbyCode, setLobbyCode] = useState<string | null>(null);
  const [lobbyPlayers, setLobbyPlayers] = useState<string[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isLoadingLobby, setIsLoadingLobby] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('solo');
  const pendingLobbyCodeUsed = useRef(false);
  
  const { isOpen: isCreatorOpen, onOpen: onCreatorOpen, onClose: onCreatorClose } = useDisclosure();
  const { isOpen: isExplorerOpen, onOpen: onExplorerOpen, onClose: onExplorerClose } = useDisclosure();
  const { isOpen: isMyCreationsOpen, onOpen: onMyCreationsOpen, onClose: onMyCreationsClose } = useDisclosure();
  const { isOpen: isStatsModalOpen, onOpen: onStatsModalOpen, onClose: onStatsModalClose } = useDisclosure();
  const toast = useToast();
  const [showDecoSelector, setShowDecoSelector] = useState(false);
  const [decoKey, setDecoKey] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
  const effectiveLobbyCode = propLobbyCode || paramLobbyCode;

  const handleCreateSyncDuel = () => {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const code = Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
    const url = `${window.location.origin}/syncduel/${code}`;
    navigator.clipboard?.writeText(url).catch(()=>{});
    toast({
      title: 'Lobby créé',
      description: 'Lien copié dans le presse-papiers. Partagez-le à votre ami !',
      status: 'success',
      duration: 4000,
    });
    navigate(`/syncduel/${code}`);
  };

  useEffect(() => {
    if (effectiveLobbyCode) {
      setRoomCode(effectiveLobbyCode.toUpperCase());
      if (user.displayName) {
        handleJoinWithCode(effectiveLobbyCode);
      }
      return;
    }
    
    if (!pendingLobbyCodeUsed.current) {
      const pending = localStorage.getItem('pendingLobbyCode');
      if (pending) {
        setRoomCode(pending.toUpperCase());
        localStorage.removeItem('pendingLobbyCode');
        pendingLobbyCodeUsed.current = true;
      }
    }
  }, [effectiveLobbyCode, user.displayName]);

  const handleJoinWithCode = async (code: string) => {
    setIsLoadingLobby(true);
    try {
      const res = await fetch(`${API_URL}/api/lobby/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, playerName: user.displayName, uid: user.uid }),
      });
      const data = await res.json();
      
      if (data.players) setLobbyPlayers(data.players);
      setLobbyCode(code);
      
      const lobbyInfoRes = await fetch(`${API_URL}/api/lobby/${code}`);
      const lobbyInfo = await lobbyInfoRes.json();
      const isLobbyOwner = lobbyInfo.hostName === user.displayName;
      
      setIsOwner(isLobbyOwner);
    } catch (error) {
      console.error('Erreur lors de la jonction au lobby:', error);
    } finally {
      setIsLoadingLobby(false);
    }
  };

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim() && newName !== user.displayName && auth.currentUser) {
      if (newName.trim().length < 3) {
        alert(t('errorNicknameTooShort'));
        return;
      }
      if (newName.trim().length > 20) {
        alert(t('errorNicknameTooLong'));
        return;
      }
      
      try {
        await updateProfile(auth.currentUser, { displayName: newName.trim() });
        await auth.currentUser.reload();
        
        try {
          const token = await auth.currentUser.getIdToken(true);
          const response = await fetch(`${API_URL}/api/user/update-name`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ displayName: newName.trim() })
          });
          
          if (!response.ok) {
            console.warn('⚠️ Erreur lors de la synchronisation du pseudo avec le backend');
            alert(t('errorMessageDefault'));
          } else {
            alert(`✅ ${t('nicknameUpdatedSuccess')}`);
            setTimeout(() => window.location.reload(), 500);
          }
        } catch (backendError) {
          console.error('❌ Erreur backend:', backendError);
          alert(t('errorMessageDefault'));
        }
      } catch (error) {
        console.error("Erreur de mise à jour du pseudo:", error);
        alert(t('errorMessageDefault'));
      }
    }
    setIsEditingName(false);
  };

  const handleCreateLobby = async () => {
    const res = await fetch(`${API_URL}/api/lobby/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerName: user.displayName, uid: user.uid }),
    });
    const data = await res.json();
    navigate(`/lobby/${data.code}`);
  };

  const handleJoinLobby = async () => {
    const raw = (roomCode || '').trim();
    if (!raw) return;

    const tryParseUrl = (val: string) => {
      try { return new URL(val); } catch { return null; }
    };

    const fromUrl = tryParseUrl(raw);
    if (fromUrl) {
      const parts = fromUrl.pathname.split('/').filter(Boolean);
      if (parts[0] === 'smartclash' && parts[1]) {
        navigate(`/smartclash/${parts[1]}`);
        return;
      }
      if (parts[0] === 'syncduel' && parts[1]) {
        navigate(`/syncduel/${parts[1].toUpperCase()}`);
        return;
      }
      if (parts[0] === 'lobby' && parts[1]) {
        navigate(`/lobby/${parts[1].toUpperCase()}`);
        return;
      }
    }

    if (raw.toLowerCase().startsWith('smartclash/')) {
      const id = raw.split('/')[1];
      if (id) { navigate(`/smartclash/${id}`); return; }
    }
    if (raw.toLowerCase().startsWith('syncduel/')) {
      const code = raw.split('/')[1];
      if (code) { navigate(`/syncduel/${code.toUpperCase()}`); return; }
    }
    if (raw.toLowerCase().startsWith('lobby/')) {
      const code = raw.split('/')[1];
      if (code) { navigate(`/lobby/${code.toUpperCase()}`); return; }
    }

    const sixCode = /^[A-Z0-9]{6}$/i;
    if (sixCode.test(raw)) {
      const code = raw.toUpperCase();
      try {
        const res = await fetch(`${API_URL}/api/lobby/${code}`);
        if (res.ok) {
          const info = await res.json();
          if (info && (info.code || info.players)) {
            navigate(`/lobby/${code}`);
            return;
          }
        }
      } catch {}
      navigate(`/syncduel/${code}`);
      return;
    }

    navigate(`/smartclash/${raw}`);
  };

  const handleLeaveLobby = async () => {
    if (lobbyCode) {
      try {
        await fetch(`${API_URL}/api/lobby/leave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            code: lobbyCode, 
            playerName: user.displayName,
            isOwner: isOwner
          }),
        });
      } catch (error) {
        console.error("Erreur lors de la notification de départ:", error);
      }
    }
    setLobbyCode(null);
    setLobbyPlayers([]);
    setIsOwner(false);
  };

  if (lobbyCode) {
    if (isLoadingLobby) {
      return (
        <div className="lobby-loading-v2">
          <div className="loading-spinner"></div>
          <p>Vérification du lobby...</p>
        </div>
      );
    }
    
    return (
      <LobbyRoomPage
        user={user}
        lobbyCode={lobbyCode}
        isOwner={isOwner}
        onLeave={handleLeaveLobby}
        onStartMultiplayerGame={onStartMultiplayerGame}
      />
    );
  }

  return (
    <div className="lobby-container-v2">
      {/* En-tête compact */}
      <div className="lobby-header-v2">
        <div className="header-left">
          <div className="user-info-v2">
            <PlayerName
              key={`header-deco-${decoKey}`}
              userId={user.uid || ''}
              userName={user.displayName || 'Anonyme'}
              checkEffect={!user.isAnonymous}
              className="user-name-v2"
            />
          </div>
        </div>

        <div className="header-right">
          <LanguageSwitcher />
          <SoundControl size="md" variant="compact" iconColor="white" />
          <HelpPopover variant="icon" size="md" />
          
          <div className="social-links-v2">
            <a href="https://discord.gg/kNwfaxaYtG" target="_blank" rel="noopener noreferrer" className="social-link-v2" title={t('joinDiscord')}>
              <img src="/discord.png" alt="Discord" className="social-icon-v2" />
            </a>
            <a href="https://ko-fi.com/neiko" target="_blank" rel="noopener noreferrer" className="social-link-v2" title={t('supportKofi')}>
              <img src="/kofi.png" alt="Ko-fi" className="social-icon-v2" />
            </a>
          </div>
          
          <button onClick={handleLogout} className="logout-button-v2">
            <span className="logout-text">{t('logoutButton')}</span>
            <span className="logout-icon">🚪</span>
          </button>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="tabs-navigation-v2">
        <button 
          className={`tab-button-v2 ${activeTab === 'solo' ? 'active' : ''}`}
          onClick={() => setActiveTab('solo')}
        >
          <span className="tab-icon">🎮</span>
          <span>Solo</span>
        </button>
        <button 
          className={`tab-button-v2 ${activeTab === 'multi' ? 'active' : ''}`}
          onClick={() => setActiveTab('multi')}
        >
          <span className="tab-icon">👥</span>
          <span>Multijoueur</span>
        </button>
        <button 
          className={`tab-button-v2 ${activeTab === 'community' ? 'active' : ''}`}
          onClick={() => setActiveTab('community')}
        >
          <span className="tab-icon">🌟</span>
          <span>Communauté</span>
        </button>
        <button 
          className={`tab-button-v2 ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="tab-icon">📊</span>
          <span>Profil</span>
        </button>
      </div>

      {/* Contenu selon l'onglet actif */}
      <div className="tab-content-v2">
        {/* Onglet SOLO */}
        {activeTab === 'solo' && (
          <div className="tab-panel-v2 fade-in">
            <h2 className="section-title-v2">Modes Solo</h2>
            <div className="game-grid-v2">
              <div className="game-card-v2">
                <div className="card-header-v2">
                  <span className="card-icon-v2">🏆</span>
                  <h3>Daily Streak</h3>
                </div>
                <p className="card-description-v2">
                  Une série de questions par jour. Une seule erreur et c'est terminé !
                </p>
                <div className="button-group-v2">
                  <button 
                    onClick={() => navigate('/daily')} 
                    className="game-button-v2 primary"
                    disabled={user.isAnonymous}
                    title={user.isAnonymous ? 'Créez un compte pour jouer au Daily Streak' : ''}
                  >
                    ⚡ Jouer
                  </button>
                  <button 
                    onClick={() => navigate('/daily/leaderboard')} 
                    className="game-button-v2 secondary"
                  >
                    📊 Classements
                  </button>
                </div>
                {user.isAnonymous && (
                  <p className="card-info-v2" style={{ marginTop: '10px', fontSize: '12px', color: 'var(--texte-secondaire)', textAlign: 'center' }}>
                    🔐 Créez un compte pour jouer
                  </p>
                )}
              </div>

              {new Date().getMonth() === 11 && (
                <div className="game-card-v2 christmas">
                  <div className="card-header-v2">
                    <span className="card-icon-v2">❄️</span>
                    <div className="card-badge-v2 event">Événement</div>
                    <h3>Calendrier de l'Avent</h3>
                  </div>
                  <p className="card-description-v2">
                    24 jours de quiz festifs ! Débloquez un effet visuel exclusif.
                  </p>
                  <button onClick={() => navigate('/christmas')} className="game-button-v2 christmas-btn">
                    🎅 Ouvrir le calendrier
                  </button>
                </div>
              )}

              {(new Date().getMonth() === 0 || new Date().getMonth() === 1) && (
                <div className="game-card-v2" style={{ borderColor: 'rgba(233,30,99,0.4)', background: 'linear-gradient(135deg, rgba(233,30,99,0.08), rgba(255,64,129,0.05))' }}>
                  <div className="card-header-v2">
                    <span className="card-icon-v2">💕</span>
                    <div className="card-badge-v2 event" style={{ background: 'linear-gradient(135deg, #ff4081, #e91e63)' }}>Saint-Valentin</div>
                    <h3>Mode Saint-Valentin</h3>
                  </div>
                  <p className="card-description-v2">
                    Daily en duo, test de compatibilité, et plus encore ! 💘
                  </p>
                  <button
                    onClick={() => navigate('/valentine')}
                    className="game-button-v2 primary"
                    style={{ background: 'linear-gradient(135deg, #ff4081, #e91e63)' }}
                    disabled={user.isAnonymous}
                  >
                    💕 C'est parti !
                  </button>
                  {user.isAnonymous && (
                    <p className="card-info-v2" style={{ marginTop: '10px', fontSize: '12px', color: 'var(--texte-secondaire)', textAlign: 'center' }}>
                      🔐 Créez un compte pour jouer
                    </p>
                  )}
                </div>
              )}

              {/* Weekly Mode */}
              <div className="game-card-v2" style={{ borderColor: 'rgba(233,30,99,0.4)', background: 'linear-gradient(135deg, rgba(233,30,99,0.08), rgba(255,64,129,0.05))' }}>
                <div className="card-header-v2">
                  <span className="card-icon-v2">🏆</span>
                  <div className="card-badge-v2 new" style={{ background: 'linear-gradient(135deg, #ff4081, #e91e63)' }}>Nouveau</div>
                  <h3>Weekly Mode</h3>
                </div>
                <p className="card-description-v2">
                  1 thème par jour, 3 vies, classement hebdomadaire. Joue chaque jour pour booster ton multiplicateur !
                </p>
                <div className="button-group-v2">
                  <button
                    onClick={() => navigate('/weekly')}
                    className="game-button-v2 primary"
                    style={{ background: 'linear-gradient(135deg, #ff4081, #e91e63)' }}
                    disabled={user.isAnonymous}
                  >
                    🏆 Jouer
                  </button>
                </div>
                {user.isAnonymous && (
                  <p className="card-info-v2" style={{ marginTop: '10px', fontSize: '12px', color: 'var(--texte-secondaire)', textAlign: 'center' }}>
                    🔐 Créez un compte pour jouer
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Onglet MULTIJOUEUR */}
        {activeTab === 'multi' && (
          <div className="tab-panel-v2 fade-in">
            <h2 className="section-title-v2">Modes Multijoueur</h2>
            
            {/* Grille des modes multi */}
            <div className="game-grid-v2">
              <div className="game-card-v2 highlight">
                <div className="card-header-v2">
                  <span className="card-icon-v2">⚡</span>
                  <h3>Sync Duel</h3>
                </div>
                <p className="card-description-v2">Défiez un ami en 1v1 temps réel. Plus vous répondez vite, plus vous gagnez de points !</p>
                <button onClick={handleCreateSyncDuel} className="game-button-v2 primary">
                  🎯 Nouveau duel
                </button>
              </div>

              <div className="game-card-v2">
                <div className="card-header-v2">
                  <span className="card-icon-v2">🎯</span>
                  <h3>{t('localGameTitle')}</h3>
                </div>
                <p className="card-description-v2">{t('localGameDescription')}</p>
                <button onClick={onStartLocalGame} className="game-button-v2 primary">
                  {t('localGameButton')}
                </button>
              </div>

              <div className="game-card-v2">
                <div className="card-header-v2">
                  <span className="card-icon-v2">🎮</span>
                  <h3>{t('createRoomTitle')}</h3>
                </div>
                <p className="card-description-v2">{t('createRoomDescription')}</p>
                <button onClick={handleCreateLobby} className="game-button-v2 primary">
                  {t('createButton')}
                </button>
              </div>

              <div className="game-card-v2">
                <div className="card-header-v2">
                  <span className="card-icon-v2">🔗</span>
                  <h3>{t('joinRoomTitle')}</h3>
                </div>
                <p className="card-description-v2">{t('joinRoomDescription')}</p>
                <form onSubmit={e => { e.preventDefault(); handleJoinLobby(); }} className="join-form-v2">
                  <input 
                    type="text" 
                    placeholder={t('roomCodePlaceholder')}
                    value={roomCode}
                    onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                    maxLength={6}
                    className="code-input-v2"
                  />
                  <button type="submit" className="game-button-v2 primary">
                    {t('joinButton')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Onglet COMMUNAUTÉ */}
        {activeTab === 'community' && (
          <div className="tab-panel-v2 fade-in">
            <h2 className="section-title-v2">Catégories Communautaires</h2>
            
            <div className="community-intro-v2">
              <p>Explorez les catégories créées par la communauté ou créez les vôtres !</p>
            </div>

            <div className="game-grid-v2">
              <div className="game-card-v2 highlight">
                <div className="card-header-v2">
                  <span className="card-icon-v2">🔍</span>
                  <h3>{t('categoryManager.explore.button')}</h3>
                </div>
                <p className="card-description-v2">
                  Parcourez les catégories créées par d'autres joueurs
                </p>
                <button onClick={onExplorerOpen} className="game-button-v2 primary">
                  Explorer
                </button>
              </div>

              {user && !user.isAnonymous ? (
                <>
                  <div className="game-card-v2">
                    <div className="card-header-v2">
                      <span className="card-icon-v2">✨</span>
                      <h3>{t('categoryManager.create.button')}</h3>
                    </div>
                    <p className="card-description-v2">
                      Créez vos propres catégories personnalisées
                    </p>
                    <button onClick={onCreatorOpen} className="game-button-v2 primary">
                      Créer
                    </button>
                  </div>

                  <div className="game-card-v2">
                    <div className="card-header-v2">
                      <span className="card-icon-v2">⚙️</span>
                      <h3>{t('categoryManager.manage.button')}</h3>
                    </div>
                    <p className="card-description-v2">
                      Gérez et modifiez vos catégories
                    </p>
                    <button onClick={onMyCreationsOpen} className="game-button-v2 primary">
                      Gérer
                    </button>
                  </div>
                </>
              ) : (
                <div className="auth-required-card-v2">
                  <div className="auth-icon-v2">🔐</div>
                  <h3>Créez un compte</h3>
                  <p>Débloquez la création et la gestion de catégories personnalisées</p>
                  <ul className="feature-list-v2">
                    <li>✨ Créer vos propres catégories</li>
                    <li>⚙️ Gérer vos créations</li>
                    <li>💾 Sauvegarder vos favoris</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet PROFIL */}
        {activeTab === 'profile' && (
          <div className="tab-panel-v2 fade-in">
            <h2 className="section-title-v2">Mon Profil</h2>
            
            {user && !user.isAnonymous ? (
              <div className="profile-content-v2">
                <div className="profile-card-v2">
                  <div className="profile-header-v2">
                    <div className="profile-avatar-v2">
                      {user.displayName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="profile-info-v2">
                      {isEditingName ? (
                        <form onSubmit={handleUpdateName} className="edit-name-form-v2 profile-edit">
                          <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="edit-name-input-v2"
                            autoFocus
                          />
                          <div className="edit-buttons-v2">
                            <button type="submit" className="edit-button-v2 save" title={t('saveButtonTitle')}>✓ Enregistrer</button>
                            <button type="button" onClick={() => setIsEditingName(false)} className="edit-button-v2 cancel" title={t('cancelButtonTitle')}>✗ Annuler</button>
                          </div>
                        </form>
                      ) : (
                        <div className="profile-name-section">
                          <h3>
                            <PlayerName
                              key={`profile-deco-${decoKey}`}
                              userId={user.uid || ''}
                              userName={user.displayName || 'Joueur'}
                              checkEffect
                            />
                          </h3>
                          <button onClick={() => setIsEditingName(true)} className="edit-profile-button" title={t('editNicknameTitle')}>
                            ✏️ Modifier le pseudo
                          </button>
                        </div>
                      )}
                      <p>{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="profile-actions-v2">
                    <button onClick={onStatsModalOpen} className="profile-action-btn action-stats">
                      <span className="action-icon">📈</span>
                      <span className="action-label">Mes statistiques</span>
                      <span className="action-arrow">›</span>
                    </button>
                    
                    <button
                      onClick={() => setShowDecoSelector(prev => !prev)}
                      className={`profile-action-btn action-deco ${showDecoSelector ? 'open' : ''}`}
                    >
                      <span className="action-icon">🎨</span>
                      <span className="action-label">Décorations de pseudo</span>
                      <span className="action-arrow">{showDecoSelector ? '‹' : '›'}</span>
                    </button>

                    {showDecoSelector && (
                      <DecorationSelector
                        userName={user.displayName || 'Joueur'}
                        onClose={() => setShowDecoSelector(false)}
                        onChange={() => setDecoKey(k => k + 1)}
                      />
                    )}
                    
                    <button onClick={handleLogout} className="profile-action-btn action-logout">
                      <span className="action-icon">🚪</span>
                      <span className="action-label">Se déconnecter</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="auth-required-card-v2 centered">
                <div className="auth-icon-v2">🔐</div>
                <h3>Créez un compte</h3>
                <p>Débloquez le suivi complet de votre progression !</p>
                <ul className="feature-list-v2">
                  <li>🏆 Suivi de vos victoires et défaites</li>
                  <li>📈 Progression par catégorie</li>
                  <li>🎯 Achievements et trophées</li>
                  <li>📊 Historique de vos parties</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modales (inchangées) */}
      <CategoryCreator isOpen={isCreatorOpen} onClose={onCreatorClose} onCategoryCreated={onCreatorClose} user={user} />
      <CategoryExplorer isOpen={isExplorerOpen} onClose={onExplorerClose} user={user} />
      <CategoryMyCreations isOpen={isMyCreationsOpen} onClose={onMyCreationsClose} user={user} />
      {user && !user.isAnonymous && (
        <PlayerStats user={user} isOpen={isStatsModalOpen} onClose={onStatsModalClose} />
      )}
    </div>
  );
};

export default LobbyPageV2;
